import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import {
  BarSeries,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
} from "lightweight-charts";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from "../../../constant/index";
import ChartingTopbar from "./ChartingTopbar";
import RestrictedScreenNotice from "../../Common/RestrictedScreenNotice";
import useIsSmallScreen from "../../../hooks/useIsSmallScreen";

const DEFAULT_SYMBOL = "Nifty 50";
const DEFAULT_TIMEFRAME = "12M";
const DEFAULT_BUCKET_TIMEFRAME = "1D";
const DEFAULT_OSCILLATORS = ["rsi", "macd"];
const DEFAULT_CHART_TYPE = "candles";
const SAVABLE_INTERVALS = ["1D", "5D", "1M", "3M", "6M", "12M", "1Y", "3Y", "5Y"];

const MA_CONFIG = {
  sma10: { label: "10-DMA", period: 10, color: "#9333ea", type: "sma" },
  sma50: { label: "50-DMA", period: 50, color: "#E01235", type: "sma" },
  sma200: { label: "200-DMA", period: 200, color: "#121211", type: "sma" },
  ema21: { label: "21-DEMA", period: 21, color: "#0f766e", type: "dema" },
};

const CHART_THEME = {
  background: "#ffffff",
  panelBackground: "#f8fafc",
  border: "#e2e8f0",
  grid: "#e2e8f0",
  text: "#475569",
  accent: "#7c3aed",
  success: "#16a34a",
  danger: "#dc2626",
  volume: "#94a3b8",
};

const baseChartOptions = {
  layout: {
    background: { type: ColorType.Solid, color: CHART_THEME.background },
    textColor: CHART_THEME.text,
    attributionLogo: false,
    fontFamily: "IBM Plex Sans, sans-serif",
  },
  localization: {
    locale: "en-US",
  },
  grid: {
    vertLines: { color: CHART_THEME.grid, style: LineStyle.SparseDotted },
    horzLines: { color: CHART_THEME.grid, style: LineStyle.SparseDotted },
  },
  crosshair: {
    mode: CrosshairMode.Normal,
    vertLine: {
      color: "#cbd5e1",
      width: 1,
      style: LineStyle.Dashed,
      labelBackgroundColor: CHART_THEME.accent,
    },
    horzLine: {
      color: "#cbd5e1",
      width: 1,
      style: LineStyle.Dashed,
      labelBackgroundColor: CHART_THEME.accent,
    },
  },
  rightPriceScale: {
    borderColor: CHART_THEME.border,
    scaleMargins: { top: 0.08, bottom: 0.08 },
  },
  timeScale: {
    borderColor: CHART_THEME.border,
    timeVisible: true,
    secondsVisible: false,
  },
  handleScroll: {
    mouseWheel: true,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: false,
  },
  handleScale: {
    axisPressedMouseMove: true,
    mouseWheel: true,
    pinch: true,
  },
};

const oscillatorChartOptions = {
  ...baseChartOptions,
  rightPriceScale: {
    borderColor: CHART_THEME.border,
    scaleMargins: { top: 0.12, bottom: 0.12 },
  },
};

const getTimeframeRange = (interval) => {
  const to = dayjs();

  switch (interval) {
    case "1D":
      return { from: to.subtract(1, "day"), to };
    case "5D":
      return { from: to.subtract(5, "day"), to };
    case "1M":
      return { from: to.subtract(1, "month"), to };
    case "3M":
      return { from: to.subtract(3, "month"), to };
    case "6M":
      return { from: to.subtract(6, "month"), to };
    case "1Y":
    case "12M":
      return { from: to.subtract(1, "year"), to };
    case "3Y":
      return { from: to.subtract(3, "year"), to };
    case "5Y":
      return { from: to.subtract(5, "year"), to };
    default:
      return { from: to.subtract(1, "year"), to };
  }
};

const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPricePoint = (point) => ({
  time: point.x,
  open: normalizeNumber(point.y?.[0]) ?? 0,
  high: normalizeNumber(point.y?.[1]) ?? 0,
  low: normalizeNumber(point.y?.[2]) ?? 0,
  close: normalizeNumber(point.y?.[3]) ?? 0,
  volume: normalizeNumber(point.volume) ?? 0,
});

const normalizeChartData = (rawData) => {
  const byTime = new Map();

  rawData.forEach((point) => {
    const normalizedPoint = toPricePoint(point);
    if (!normalizedPoint.time) {
      return;
    }

    // Lightweight Charts requires strictly ascending unique times.
    // If the backend returns duplicate dates, keep the latest occurrence.
    byTime.set(normalizedPoint.time, normalizedPoint);
  });

  return Array.from(byTime.values()).sort((left, right) =>
    dayjs(left.time).valueOf() - dayjs(right.time).valueOf()
  );
};

const sanitizeSavedInterval = (interval) =>
  SAVABLE_INTERVALS.includes(interval) ? interval : DEFAULT_TIMEFRAME;

const getLastWorkingDayOfPreviousWeek = (anchorDate) => {
  const date = dayjs(anchorDate);
  const dayOfWeek = date.day();

  if (dayOfWeek === 0) {
    return date.subtract(9, "day");
  }

  return date.subtract(dayOfWeek + 2, "day");
};

const getSnapshotRange = (timeframe, anchorDate = dayjs()) => {
  const to = dayjs(anchorDate);

  switch (timeframe) {
    case "7D":
      return { from: getLastWorkingDayOfPreviousWeek(to), to };
    case "30D":
      return { from: to.subtract(29, "day"), to };
    case "1D":
    default:
      return { from: to, to };
  }
};

const toLinePoint = (time, value) => ({
  time,
  value: value == null ? null : Number(value.toFixed(2)),
});

const calcSMA = (data, period) => {
  let sum = 0;

  return data.map((point, index) => {
    sum += point.close;
    if (index >= period) {
      sum -= data[index - period].close;
    }

    if (index < period - 1) {
      return toLinePoint(point.time, null);
    }

    return toLinePoint(point.time, sum / period);
  });
};

const calcEMAValues = (values, period) => {
  if (!values.length) {
    return [];
  }

  const multiplier = 2 / (period + 1);
  let previous = values[0];

  return values.map((value, index) => {
    if (index === 0) {
      return value;
    }

    previous = value * multiplier + previous * (1 - multiplier);
    return previous;
  });
};

const calcDEMA = (data, period) => {
  const closes = data.map((point) => point.close);
  const ema = calcEMAValues(closes, period);
  const emaOfEma = calcEMAValues(ema, period);

  return data.map((point, index) => {
    if (index < period - 1) {
      return toLinePoint(point.time, null);
    }

    return toLinePoint(point.time, 2 * ema[index] - emaOfEma[index]);
  });
};

const calcRSI = (data, period = 14) => {
  if (data.length < period + 1) {
    return data.map((point) => toLinePoint(point.time, null));
  }

  const result = [];
  let gain = 0;
  let loss = 0;

  result.push(toLinePoint(data[0].time, null));

  for (let index = 1; index <= period; index += 1) {
    const diff = data[index].close - data[index - 1].close;
    if (diff > 0) {
      gain += diff;
    } else {
      loss -= diff;
    }

    result.push(toLinePoint(data[index].time, null));
  }

  let avgGain = gain / period;
  let avgLoss = loss / period;

  for (let index = period + 1; index < data.length; index += 1) {
    const diff = data[index].close - data[index - 1].close;
    avgGain = (avgGain * (period - 1) + Math.max(diff, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-diff, 0)) / period;

    const rs = avgGain / (avgLoss || 1);
    result.push(toLinePoint(data[index].time, 100 - 100 / (1 + rs)));
  }

  return result;
};

const calcMACD = (data) => {
  const closes = data.map((point) => point.close);
  const ema12 = calcEMAValues(closes, 12);
  const ema26 = calcEMAValues(closes, 26);
  const macdValues = closes.map((_, index) => ema12[index] - ema26[index]);
  const signalValues = calcEMAValues(macdValues, 9);

  return {
    macd: data.map((point, index) => toLinePoint(point.time, macdValues[index])),
    signal: data.map((point, index) => toLinePoint(point.time, signalValues[index])),
    histogram: data.map((point, index) => ({
      time: point.time,
      value: Number((macdValues[index] - signalValues[index]).toFixed(2)),
      color:
        macdValues[index] - signalValues[index] >= 0
          ? "rgba(22, 163, 74, 0.55)"
          : "rgba(220, 38, 38, 0.55)",
    })),
  };
};

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);

const summarizeRangeOHLC = (data) => {
  if (!data.length) {
    return null;
  }

  return {
    time: data[data.length - 1].time,
    open: data[0].open,
    high: Math.max(...data.map((point) => point.high)),
    low: Math.min(...data.map((point) => point.low)),
    close: data[data.length - 1].close,
    volume: data.reduce((sum, point) => sum + point.volume, 0),
  };
};

const AdvancedChartingContent = () => {
  const { token } = useAuth();

  const [allSymbols, setAllSymbols] = useState([DEFAULT_SYMBOL]);
  const [ohlcData, setOhlcData] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(DEFAULT_SYMBOL);
  const [chartType, setChartType] = useState(DEFAULT_CHART_TYPE);
  const [interval, setSelectedInterval] = useState(DEFAULT_TIMEFRAME);
  const [timeframe, setTimeframe] = useState(DEFAULT_BUCKET_TIMEFRAME);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMAs, setSelectedMAs] = useState(["sma50", "sma200"]);
  const [selectedOscillators, setSelectedOscillators] = useState(DEFAULT_OSCILLATORS);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [timeframeMode, setTimeframeMode] = useState(false);

  const cacheRef = useRef({});
  const mountedRef = useRef(true);
  const preferenceHydratedRef = useRef(false);
  const lastPersistableIntervalRef = useRef(DEFAULT_TIMEFRAME);
  const priceChartHostRef = useRef(null);
  const rsiChartHostRef = useRef(null);
  const macdChartHostRef = useRef(null);
  const chartsRef = useRef([]);
  const savePreferenceDebouncedRef = useRef(null);

  const fetchData = async (params) => {
    const key = JSON.stringify(params);

    if (cacheRef.current[key]) {
      if (mountedRef.current) {
        setOhlcData(cacheRef.current[key]);
      }
      return;
    }

    if (mountedRef.current) {
      setLoading(true);
    }

    try {
      const { data } = await axios.post(`${BASE_URL}/ohlc`, params, {
        headers: { Authorization: `Bearer ${token}` },
      });

      cacheRef.current[key] = data;
      if (mountedRef.current) {
        setOhlcData(data);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const debouncedFetchRef = useRef(null);

  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce((params) => {
      fetchData(params);
    }, 250);
  }

  if (!savePreferenceDebouncedRef.current) {
    savePreferenceDebouncedRef.current = debounce(async ({ authToken, payload }) => {
      await axios.post(`${BASE_URL}/advancedChartingPreference`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    }, 400);
  }

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      debouncedFetchRef.current?.cancel();
      savePreferenceDebouncedRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const loadSymbols = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/getSymbolList`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { _: Date.now() },
        });

        if (mountedRef.current && Array.isArray(data) && data.length) {
          setAllSymbols(data);
        }
      } catch (error) {
        if (mountedRef.current) {
          setAllSymbols([DEFAULT_SYMBOL]);
        }
      }
    };

    const loadInitialState = async () => {
      let savedPreference = null;

      try {
        const { data } = await axios.get(`${BASE_URL}/advancedChartingPreference`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { _: Date.now() },
        });
        savedPreference = data;
      } catch (error) {
        savedPreference = null;
      }

      const initialInterval = sanitizeSavedInterval(savedPreference?.interval);
      const initialChartType =
        savedPreference?.chartType === "bars" ? "bars" : DEFAULT_CHART_TYPE;
      const initialIndicators = Array.isArray(savedPreference?.indicators)
        ? savedPreference.indicators
        : ["sma50", "sma200"];
      const initialOscillators = Array.isArray(savedPreference?.oscillators)
        ? savedPreference.oscillators
        : DEFAULT_OSCILLATORS;

      const { from, to } = getTimeframeRange(initialInterval);
      const formattedFrom = from.format("YYYY-MM-DD");
      const formattedTo = to.format("YYYY-MM-DD");

      if (mountedRef.current) {
        setSelectedInterval(initialInterval);
        setChartType(initialChartType);
        setSelectedMAs(initialIndicators);
        setSelectedOscillators(initialOscillators);
        setFromDate(formattedFrom);
        setToDate(formattedTo);
      }

      lastPersistableIntervalRef.current = initialInterval;

      await fetchData({
        symbol: DEFAULT_SYMBOL,
        fromDate: formattedFrom,
        toDate: formattedTo,
        interval: initialInterval,
      });

      preferenceHydratedRef.current = true;
    };

    loadSymbols();
    loadInitialState();
  }, [token]);

  useEffect(() => {
    if (!token || !preferenceHydratedRef.current) {
      return;
    }

    savePreferenceDebouncedRef.current?.({
      authToken: token,
      payload: {
        interval:
          interval === "custom"
            ? lastPersistableIntervalRef.current
            : sanitizeSavedInterval(interval),
        chartType,
        indicators: selectedMAs,
        oscillators: selectedOscillators,
      },
    });
  }, [chartType, interval, selectedMAs, selectedOscillators, token]);

  const rawChartData = useMemo(() => normalizeChartData(ohlcData), [ohlcData]);
  const chartData = rawChartData;
  const rangeSummaryPoint = useMemo(
    () => summarizeRangeOHLC(chartData),
    [chartData]
  );
  const rsiData = useMemo(() => calcRSI(chartData), [chartData]);
  const macdData = useMemo(() => calcMACD(chartData), [chartData]);

  const movingAverageSeries = useMemo(() => {
    return selectedMAs
      .map((key) => {
        const config = MA_CONFIG[key];
        if (!config) {
          return null;
        }

        const data =
          config.type === "dema"
            ? calcDEMA(chartData, config.period)
            : calcSMA(chartData, config.period);

        return { ...config, key, data };
      })
      .filter(Boolean);
  }, [chartData, selectedMAs]);

  useEffect(() => {
    if (!timeframeMode || !rawChartData.length) {
      return;
    }

    const actualFrom = rawChartData[0]?.time ?? null;
    const actualTo = rawChartData[rawChartData.length - 1]?.time ?? null;

    if (actualFrom && actualFrom !== fromDate) {
      setFromDate(actualFrom);
    }

    if (actualTo && actualTo !== toDate) {
      setToDate(actualTo);
    }
  }, [fromDate, rawChartData, timeframeMode, toDate]);

  useEffect(() => {
    if (!priceChartHostRef.current) {
      return undefined;
    }

    chartsRef.current.forEach(({ cleanup }) => cleanup?.());
    chartsRef.current = [];

    const chartEntries = [];
    let isSyncing = false;

    const buildChart = (container, height, options = baseChartOptions) => {
      if (!container) {
        return null;
      }

      const chart = createChart(container, {
        ...options,
        width: container.clientWidth,
        height,
      });

      chartEntries.push({ chart });
      return chart;
    };

    const priceChart = buildChart(priceChartHostRef.current, 420, baseChartOptions);
    if (!priceChart) {
      return undefined;
    }

    const priceSeriesDefinition =
      chartType === "bars" ? BarSeries : CandlestickSeries;

    const priceSeriesOptions =
      chartType === "bars"
        ? {
            upColor: CHART_THEME.success,
            downColor: CHART_THEME.danger,
            thinBars: true,
            openVisible: true,
            priceLineVisible: true,
            lastValueVisible: true,
          }
        : {
            upColor: CHART_THEME.success,
            downColor: CHART_THEME.danger,
            wickUpColor: CHART_THEME.success,
            wickDownColor: CHART_THEME.danger,
            borderVisible: false,
            priceLineVisible: true,
            lastValueVisible: true,
          };

    const priceSeries = priceChart.addSeries(priceSeriesDefinition, priceSeriesOptions);

    const volumeSeries = priceChart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
      color: CHART_THEME.volume,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    priceChart.priceScale("").applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
    });

    priceSeries.setData(
      chartData.map(({ time, open, high, low, close }) => ({
        time,
        open,
        high,
        low,
        close,
      }))
    );

    const handleCrosshairMove = (param) => {
      const hoveredSeriesData = param?.seriesData?.get(priceSeries);
      if (!hoveredSeriesData) {
        setHoveredPoint(null);
        return;
      }

      setHoveredPoint({
        time: param.time ?? null,
        open: hoveredSeriesData.open,
        high: hoveredSeriesData.high,
        low: hoveredSeriesData.low,
        close: hoveredSeriesData.close,
        volume:
          chartData[param.logical ?? -1]?.volume ??
          chartData.find((point) => point.time === param.time)?.volume ??
          0,
      });
    };

    priceChart.subscribeCrosshairMove(handleCrosshairMove);

    volumeSeries.setData(
      chartData.map(({ time, open, close, volume }) => ({
        time,
        value: volume,
        color:
          close >= open ? "rgba(22, 163, 74, 0.35)" : "rgba(220, 38, 38, 0.35)",
      }))
    );

    movingAverageSeries.forEach((seriesConfig) => {
      const lineSeries = priceChart.addSeries(LineSeries, {
        color: seriesConfig.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        title: seriesConfig.label,
      });

      lineSeries.setData(seriesConfig.data.filter((point) => point.value != null));
    });

    let rsiChart = null;
    if (selectedOscillators.includes("rsi") && rsiChartHostRef.current) {
      rsiChart = buildChart(rsiChartHostRef.current, 160, oscillatorChartOptions);

      const rsiSeries = rsiChart.addSeries(LineSeries, {
        color: CHART_THEME.accent,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        title: "RSI (14)",
      });

      const upperLine = rsiChart.addSeries(LineSeries, {
        color: "rgba(124, 58, 237, 0.28)",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      const lowerLine = rsiChart.addSeries(LineSeries, {
        color: "rgba(124, 58, 237, 0.28)",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      const rsiBase = chartData.map(({ time }) => ({ time }));

      rsiSeries.setData(rsiData.filter((point) => point.value != null));
      upperLine.setData(rsiBase.map(({ time }) => ({ time, value: 70 })));
      lowerLine.setData(rsiBase.map(({ time }) => ({ time, value: 30 })));

      rsiChart.priceScale("right").applyOptions({
        autoScale: false,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      });
      rsiChart.timeScale().fitContent();
    }

    let macdChart = null;
    if (selectedOscillators.includes("macd") && macdChartHostRef.current) {
      macdChart = buildChart(macdChartHostRef.current, 200, oscillatorChartOptions);

      const histogramSeries = macdChart.addSeries(HistogramSeries, {
        priceLineVisible: false,
        lastValueVisible: false,
      });

      const macdLineSeries = macdChart.addSeries(LineSeries, {
        color: "#0f766e",
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        title: "MACD",
      });

      const signalLineSeries = macdChart.addSeries(LineSeries, {
        color: "#f97316",
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        title: "Signal",
      });

      histogramSeries.setData(macdData.histogram);
      macdLineSeries.setData(macdData.macd);
      signalLineSeries.setData(macdData.signal);
      macdChart.timeScale().fitContent();
    }

    const syncCharts = [priceChart, rsiChart, macdChart].filter(Boolean);

    syncCharts.forEach((chart) => {
      const subscription = (range) => {
        if (isSyncing || !range) {
          return;
        }

        isSyncing = true;
        syncCharts.forEach((targetChart) => {
          if (targetChart !== chart) {
            targetChart.timeScale().setVisibleLogicalRange(range);
          }
        });
        isSyncing = false;
      };

      chart.timeScale().subscribeVisibleLogicalRangeChange(subscription);
      chartEntries.push({
        cleanup: () => chart.timeScale().unsubscribeVisibleLogicalRangeChange(subscription),
      });
    });

    const fitPriceChart = () => {
      priceChart.timeScale().fitContent();
    };

    const resizeObserver = new ResizeObserver(() => {
      syncCharts.forEach((chart, index) => {
        const host = [priceChartHostRef, rsiChartHostRef, macdChartHostRef][index]?.current;
        if (host) {
          chart.applyOptions({ width: host.clientWidth });
        }
      });
    });

    [priceChartHostRef.current, rsiChartHostRef.current, macdChartHostRef.current]
      .filter(Boolean)
      .forEach((element) => resizeObserver.observe(element));

    fitPriceChart();

    chartsRef.current = [
      ...chartEntries,
      {
        cleanup: () => priceChart.unsubscribeCrosshairMove(handleCrosshairMove),
      },
      {
        cleanup: () => resizeObserver.disconnect(),
      },
      ...syncCharts.map((chart) => ({
        cleanup: () => chart.remove(),
      })),
    ];

    return () => {
      chartsRef.current.forEach(({ cleanup }) => cleanup?.());
      chartsRef.current = [];
    };
  }, [chartData, chartType, movingAverageSeries, selectedOscillators]);

  const requestData = (nextValues) => {
    debouncedFetchRef.current?.({
      symbol: nextValues.symbol ?? selectedSymbol,
      interval: nextValues.interval ?? interval,
      fromDate: nextValues.fromDate ?? fromDate,
      toDate: nextValues.toDate ?? toDate,
    });
  };

  const handleSymbolSelection = (value) => {
    if (!value) {
      return;
    }

    setSelectedSymbol(value);
    requestData({ symbol: value });
  };

  const handleIntervalChange = (event) => {
    const nextInterval = event.target.value;
    setTimeframeMode(false);
    setSelectedInterval(nextInterval);
    if (SAVABLE_INTERVALS.includes(nextInterval)) {
      lastPersistableIntervalRef.current = nextInterval;
    }

    if (nextInterval === "custom") {
      requestData({ interval: "custom" });
      return;
    }

    const { from, to } = getTimeframeRange(nextInterval);
    const formattedFrom = from.format("YYYY-MM-DD");
    const formattedTo = to.format("YYYY-MM-DD");

    setFromDate(formattedFrom);
    setToDate(formattedTo);
    requestData({
      interval: nextInterval,
      fromDate: formattedFrom,
      toDate: formattedTo,
    });
  };

  const handleFromDateChange = (value) => {
    const formatted = value ? value.format("YYYY-MM-DD") : null;
    setTimeframeMode(false);
    setFromDate(formatted);
    setSelectedInterval("custom");
    requestData({ fromDate: formatted, interval: "custom" });
  };

  const handleToDateChange = (value) => {
    const formatted = value ? value.format("YYYY-MM-DD") : null;
    setTimeframeMode(false);
    setToDate(formatted);
    setSelectedInterval("custom");
    requestData({ toDate: formatted, interval: "custom" });
  };

  const handleTimeframeChange = (event) => {
    const nextTimeframe = event.target.value;
    const latestAvailableDate = rawChartData[rawChartData.length - 1]?.time;
    const anchorDate = latestAvailableDate ? dayjs(latestAvailableDate) : dayjs();
    const { from, to } = getSnapshotRange(nextTimeframe, anchorDate);
    const formattedFrom = from.format("YYYY-MM-DD");
    const formattedTo = to.format("YYYY-MM-DD");

    setTimeframe(nextTimeframe);
    setTimeframeMode(true);
    setFromDate(formattedFrom);
    setToDate(formattedTo);
    setSelectedInterval("custom");

    requestData({
      fromDate: formattedFrom,
      toDate: formattedTo,
      interval: "custom",
    });
  };

  const latestPoint = chartData[chartData.length - 1];
  const activePoint =
    timeframe === "1D"
      ? hoveredPoint || latestPoint
      : rangeSummaryPoint;
  const previousPoint = chartData[chartData.length - 2];
  const priceChange = latestPoint && previousPoint ? latestPoint.close - previousPoint.close : 0;
  const changePercent =
    latestPoint && previousPoint?.close
      ? (priceChange / previousPoint.close) * 100
      : 0;

  return (
    <div style={{ background: CHART_THEME.panelBackground, minHeight: "100vh" }}>
      <ChartingTopbar
        allSymbols={allSymbols}
        selectedSymbol={selectedSymbol}
        onSymbolSelection={handleSymbolSelection}
        chartType={chartType}
        onChartTypeChange={(event) => setChartType(event.target.value)}
        interval={interval}
        onIntervalChange={handleIntervalChange}
        timeframe={timeframe}
        onTimeframeChange={handleTimeframeChange}
        selectedMAs={selectedMAs}
        onMAChange={(event) => setSelectedMAs(event.target.value)}
        selectedOscillators={selectedOscillators}
        onOscillatorChange={(event) => setSelectedOscillators(event.target.value)}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
      />

      <div style={{ padding: "18px 20px 28px" }}>
        <section
          style={{
            background: "#ffffff",
            border: `1px solid ${CHART_THEME.border}`,
            borderRadius: 20,
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              padding: "18px 20px 12px",
              borderBottom: `1px solid ${CHART_THEME.border}`,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: "10px"}}>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a" }}>{selectedSymbol}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                {latestPoint?.close?.toFixed(2) ?? "--"}
              </div>
              <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: priceChange >= 0 ? CHART_THEME.success : CHART_THEME.danger,
                  }}
              >
                  {latestPoint
                    ? `${priceChange >= 0 ? "+" : ""}${priceChange.toFixed(2)} (${changePercent.toFixed(2)}%)`
                    : "Waiting for data"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase" }}>OHLC</div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  <span>O {activePoint?.open?.toFixed(2) ?? "--"}</span>
                  <span>H {activePoint?.high?.toFixed(2) ?? "--"}</span>
                  <span>L {activePoint?.low?.toFixed(2) ?? "--"}</span>
                  <span>C {activePoint?.close?.toFixed(2) ?? "--"}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase" }}>Range</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                  {fromDate ?? "--"} to {toDate ?? "--"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase" }}>Volume</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                  {activePoint ? formatCompactNumber(activePoint.volume) : "--"}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: 16 }}>
            <div
              ref={priceChartHostRef}
              style={{
                width: "100%",
                height: 420,
                borderRadius: 16,
                overflow: "hidden",
                background: "#ffffff",
              }}
            />

            {selectedOscillators.includes("rsi") && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: 8,
                    letterSpacing: "0.06em",
                  }}
                >
                  RSI (14)
                </div>
                <div
                  ref={rsiChartHostRef}
                  style={{
                    width: "100%",
                    height: 160,
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#ffffff",
                  }}
                />
              </div>
            )}

            {selectedOscillators.includes("macd") && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: 8,
                    letterSpacing: "0.06em",
                  }}
                >
                  MACD (12, 26, 9)
                </div>
                <div
                  ref={macdChartHostRef}
                  style={{
                    width: "100%",
                    height: 200,
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#ffffff",
                  }}
                />
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const AdvancedChartingPage = () => {
  const isSmallScreen = useIsSmallScreen();

  if (isSmallScreen) {
    return (
      <div className="container restricted-screen-layout">
        <RestrictedScreenNotice title="Advanced Charting" />
      </div>
    );
  }

  return <AdvancedChartingContent />;
};

export default AdvancedChartingPage;
